import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FaSyncAlt, FaDownload, FaCopy, FaMinus, FaPlus, FaTimes, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState({ type: '', startDate: '', endDate: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null); 
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Modal State Management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isModalPaying, setIsModalPaying] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchTransactions = async () => {
    setIsLoading(true);
    const params = {};
    if (filter.type) params.type = filter.type;
    if (filter.startDate && filter.endDate) {
      params.dateRange = `${filter.startDate},${filter.endDate}`;
    }

    try {
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', { params });
      
      // Kept filter matching pending withdrawal sync flows and active receipts
      const filteredTransactions = (response.data.transactions || []).filter(
        tx => 
          (tx.mpesaReceiptNumber && tx.mpesaReceiptNumber.trim() !== '') || 
          tx.status === 'deriv_pending' || 
          tx.status === 'mpesa_processing' ||
          tx.transactionType === 'withdrawal'
      );
      setTransactions(filteredTransactions);
      setError('');
    } catch (error) {
      console.error('Error fetching transactions:', error.message);
      setError('Failed to fetch transactions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filter]);

  // Handle checking Deriv status
  // Handle checking Deriv status
  const handleCheckDerivStatus = async (e, transactionId) => {
    if (e) e.stopPropagation();
    
    if (!transactionId || transactionId === 'undefined') {
      alert('Error: Missing structural transaction ID for verification routing.');
      return;
    }

    setSyncingId(transactionId);
    try {
      // 1. Retrieve the token exactly like Overview does
      const token = localStorage.getItem('token');
      
      // 2. Perform the GET request with Authorization Headers
      const response = await axios.get(
        `https://sys.instant-m-transfer.com/api/admin/withdraw/status/${transactionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // 3. Fallback extraction pattern: accommodates both { success, data } envelopes or naked returns
      const dataPayload = response.data && response.data.success !== undefined
        ? response.data.data 
        : response.data;

      if (dataPayload) {
        // 4. Update the local Transactions list state immediately (Mirroring Overview's filtering/mapping style)
        setTransactions(prevTransactions => 
          prevTransactions.map(tx => {
            const currentId = tx._id || tx.id;
            if (currentId === transactionId) {
              return { 
                ...tx, 
                status: dataPayload.status || dataPayload.currentStatus || tx.status,
                derivTransactionId: dataPayload.derivTransactionId || tx.derivTransactionId 
              };
            }
            return tx;
          })
        );

        // 5. Open Modal with the verified payload
        setModalData(dataPayload);
        setIsModalOpen(true);
      } else {
        alert('Status check succeeded, but backend returned an empty response structure.');
      }
    } catch (err) {
      console.error('Status check failure:', err);
      // Alert fallback mirroring the manual deposit/retry patterns in Overview
      alert(err.response?.data?.error || err.message || 'Failed to sync remote status mapping');
    } finally {
      setSyncingId(null);
    }
  };

  const handleRetryB2C = async (e, transactionId) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to process this M-Pesa B2C Payout to the customer?')) return;

    const isTriggeredFromModal = isModalOpen && (modalData?.transactionId === transactionId || modalData?._id === transactionId);
    if (isTriggeredFromModal) {
      setIsModalPaying(true);
    } else {
      setSyncingId(transactionId);
    }

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/retry-b2c`, 
        {}, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setTransactions(prev => prev.map(tx => {
          const tid = tx._id || tx.id || tx.transactionId;
          return (tid === transactionId)
            ? { ...tx, status: 'mpesa_processing', failureReason: null }
            : tx;
        }));
        
        alert('✅ M-Pesa Payout initiated successfully! Awaiting callback confirmation.');
        
        if (isTriggeredFromModal) {
          setIsModalOpen(false);
          setModalData(null);
        }
      }
    } catch (err) {
      console.error('B2C Trigger Error:', err);
      alert(err.response?.data?.error || 'Disbursement routing infrastructure failed');
    } finally {
      setSyncingId(null);
      setIsModalPaying(false);
    }
  };

  const columns = useMemo(
    () => [
      { 
        Header: 'CR Number', 
        accessor: 'accountReference',
        Cell: ({ value }) => (
          <span className="text-blue-600 font-sfts-bold text-sm sm:text-xs ">{value}</span>
        )
      },
      { 
        Header: 'Deriv ID', 
        accessor: 'derivTransactionId',
        Cell: ({ value }) => (
          <div className="flex items-center gap-1">
            <span className="font-mono text-gray-700 text-[10px] sm:text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              {value || '—'}
            </span>
          </div>
        )
      },
      { 
        Header: 'M-Pesa', 
        accessor: 'mpesaReceiptNumber',
        Cell: ({ value, row }) => (
          <div className="flex items-center gap-1">
            <span className="font-sftsMedium text-[7px] sm:text-xs">
              {value || (row.original.status === 'mpesa_processing' ? 'Awaiting Payout' : 'Pending')}
            </span>
            {value && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(value);
                }}
                className="text-gray-400 hover:text-gray-600"
                title="Copy receipt"
              >
                <FaCopy size={10} />
              </button>
            )}
          </div>
        )
      },
      { 
        Header: 'Type', 
        accessor: 'transactionType',
        Cell: ({ value }) => (
          <span className={`px-1.5 py-0.5 rounded-full text-[6px] font-sfts-bold sm:text-xs ${
            value === 'deposit'
              ? 'bg-green-100 text-green-800'
              : 'bg-orange-100 text-orange-800'
          }`}>
            {(value || 'deposit').toUpperCase()}
          </span>
        )
      },
      {
        Header: 'KES',
        accessor: 'amount',
        Cell: ({ value }) => (
          <strong className="text-[7px] sm:text-sm font-sftsBold">
            {typeof value === 'number' ? value.toLocaleString('en-US') : '0'}
          </strong>
        )
      },
      {
        Header: 'USD',
        accessor: 'usdAmount',
        Cell: ({ value }) => (
          <strong className="text-green-600 text-[7px] sm:text-sm">
            {typeof value === 'number' ? `$${value.toFixed(2)}` : '$0.00'}
          </strong>
        )
      },
      {
        Header: 'Phone', 
        accessor: 'phoneNumber',
        Cell: ({ value }) => value ? (
          <div className="flex items-center gap-1">
            <span className="font-sftsMedium text-[7px] sm:text-xs">{`${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
              }}
              className="text-gray-400 hover:text-gray-600"
              title="Copy phone"
            >
              <FaCopy size={10} />
            </button>
          </div>
        ) : '—'
      },
      {
        Header: 'Date',
        accessor: 'transactionDate',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'Invalid Date';
          return date.toLocaleDateString('en-US', {
            timeZone: 'Africa/Nairobi',
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
          });
        }
      },
      {
        Header: 'Time',
        accessor: 'transactionDateTime',
        Cell: ({ row }) => {
          const value = row.original.transactionDate;
          if (!value) return 'N/A';
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'Invalid Date';
          return date.toLocaleTimeString('en-US', {
            timeZone: 'Africa/Nairobi',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => {
          const tx = row.original;
          // Robust fallback matching how Overview tracks transaction identification structures
          const currentId = tx ? (tx._id || tx.id || tx.transactionId) : null;
          const isCurrentSyncing = syncingId === currentId;

          if (tx && tx.transactionType === 'withdrawal') {
            return (
              <button
                disabled={isCurrentSyncing || !currentId}
                onClick={(e) => handleCheckDerivStatus(e, currentId)}
                className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded text-[8px] sm:text-xs transition-colors duration-150"
              >
                {isCurrentSyncing ? 'Syncing...' : 'Check Status'}
              </button>
            );
          }

          return <span className="text-gray-400 text-xs">—</span>;
        }
      }
    ],
    [syncingId, transactions]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: transactions,
      initialState: {
        pageSize: 20,
        sortBy: [{ id: 'transactionDate', desc: true }]
      }
    },
    useSortBy,
    usePagination
  );

  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setError('');
    const params = {};
    if (filter.type) params.type = filter.type;
    if (filter.startDate && filter.endDate) {
      params.dateRange = `${filter.startDate},${filter.endDate}`;
    }

    axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', { params })
      .then(response => {
        const filteredTransactions = (response.data.transactions || []).filter(
          tx => 
            (tx.mpesaReceiptNumber && tx.mpesaReceiptNumber.trim() !== '') || 
            tx.status === 'deriv_pending' || 
            tx.status === 'mpesa_processing' ||
            tx.transactionType === 'withdrawal'
        );
        setTransactions(filteredTransactions);
        setError('');
      })
      .catch(error => {
        console.error('Error fetching transactions:', error.message);
        setError('Failed to fetch transactions');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleExportCSV = () => {
    const headers = columns.map(col => col.Header).filter(h => h !== 'Actions');
    const csvRows = [
      headers.join(','), 
      ...transactions.map(row => {
        const date = row.transactionDate ? new Date(row.transactionDate) : null;
        return [
          row.accountReference || 'N/A',
          row.derivTransactionId || 'N/A',
          row.mpesaReceiptNumber || 'N/A',
          row.transactionType || 'N/A',
          typeof row.amount === 'number' ? row.amount.toLocaleString('en-US') : 'N/A',
          typeof row.usdAmount === 'number' ? row.usdAmount.toFixed(2) : 'N/A',
          row.phoneNumber ? `+${row.phoneNumber.slice(0, 3)} ${row.phoneNumber.slice(3, 6)} ${row.phoneNumber.slice(6)}` : 'N/A',
          date && !isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { timeZone: 'Africa/Nairobi' }) : 'N/A',
          date && !isNaN(date.getTime()) ? date.toLocaleTimeString('en-US', { timeZone: 'Africa/Nairobi', hour: '2-digit', minute: '2-digit', hour12: false }) : 'N/A'
        ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (error) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="bg-red-50 border-l-2 border-red-500 p-3 rounded-r shadow text-red-700 text-xs font-medium text-center">
        {error}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12">
        <LoadingImpulse size="lg" color="blue" />
      </div>
    );
  }

  if (!transactions.length) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="text-center text-gray-500">
        <span className="material-icons text-6xl text-gray-300 mb-2">receipt</span>
        <p className="text-sm italic">No transactions found</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4">
      <div className="max-w-full mx-auto sm:max-w-3xl md:max-w-6xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h1 className="text-base font-sfts-bold text-gray-800 sm:text-xl md:text-2xl font-sfts-title">
            Transactions
          </h1>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-xs font-medium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sfts"
            >
              <FaDownload />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
            <button
              onClick={handleRefresh}
              className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-xs font-medium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sfts"
            >
              <FaSyncAlt className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <select
            name="type"
            value={filter.type}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sfts"
          >
            <option value="">All Types</option>
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
          </select>
          <input
            type="date"
            name="startDate"
            value={filter.startDate}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sfts"
          />
          <input
            type="date"
            name="endDate"
            value={filter.endDate}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sfts"
          />
        </div>

        {/* Mobile View - Card Layout */}
          {isMobile ? (
          <div className="space-y-3">
            {page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.has(row.id);
              const rowData = row.original;
              
              // Safe fallback for identifier property extractions
              const currentId = rowData ? (rowData._id || rowData.id || rowData.transactionId) : null;
              const isCurrentSyncing = syncingId === currentId;

              return (
                <div key={row.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div 
                    className="p-3 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleRowExpansion(row.id)}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-sfts-bold ${
                          rowData.transactionType === 'deposit'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {rowData.transactionType.toUpperCase()}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {new Date(rowData.transactionDate).toLocaleTimeString('en-US', {
                            timeZone: 'Africa/Nairobi',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-sfts-bold text-gray-800">
                          KES {typeof rowData.amount === 'number' ? rowData.amount.toLocaleString('en-US') : 'N/A'}
                        </span>
                        <span className="text-[10px] font-sfts-bold text-green-600">
                          {typeof rowData.usdAmount === 'number' ? `$${rowData.usdAmount.toFixed(2)}` : 'N/A'}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-600 mt-1">
                        <span className=" text-blue-600">{rowData.accountReference}</span>
                        <span className="mx-1">•</span>
                        <span>{rowData.mpesaReceiptNumber || (rowData.status === 'mpesa_processing' ? 'Awaiting Payout' : 'Pending')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      {rowData.transactionType === 'withdrawal' && (
                        <button
                          disabled={isCurrentSyncing}
                          onClick={(e) => handleCheckDerivStatus(e, currentId)}
                          className="px-2 py-1 bg-yellow-500 text-white text-[9px] font-bold rounded shadow-sm hover:bg-yellow-600 transition-colors"
                        >
                          {isCurrentSyncing ? 'Syncing...' : 'Check Status'}
                        </button>
                      )}
                      <button className="text-red-500" onClick={() => toggleRowExpansion(row.id)}>
                        {isExpanded ? <FaMinus size={12} /> : <FaPlus size={12} />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-2 pt-3 text-[10px]">
                        <div>
                          <div className="text-gray-500 font-medium mb-0.5">Date</div>
                          <div className="text-gray-800">
                            {new Date(rowData.transactionDate).toLocaleDateString('en-US', {
                              timeZone: 'Africa/Nairobi',
                              year: '2-digit',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-medium mb-0.5">Phone</div>
                          <div className="flex items-center gap-1">
                            <span>{rowData.phoneNumber ? `+${rowData.phoneNumber.slice(0, 3)} ${rowData.phoneNumber.slice(3, 6)} ${rowData.phoneNumber.slice(6)}` : '—'}</span>
                            {rowData.phoneNumber && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(rowData.phoneNumber);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <FaCopy size={8} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-medium mb-0.5">Deriv Tx Ref</div>
                          <div className="text-gray-800 font-mono text-[9px]">
                            {rowData.derivTransactionId || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-medium mb-0.5">M-Pesa Receipt</div>
                          <div className="flex items-center gap-1">
                            <span>{rowData.mpesaReceiptNumber || 'N/A'}</span>
                            {rowData.mpesaReceiptNumber && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(rowData.mpesaReceiptNumber);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <FaCopy size={8} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop View - Table Layout */
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="w-full">
                <thead className="bg-gray-800 text-white">
                  {headerGroups.map(headerGroup => {
                    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={key} {...headerGroupProps}>
                        {headerGroup.headers.map(column => {
                          const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                          return (
                            <th
                              key={columnKey}
                              {...columnProps}
                              className={`p-0.25 text-left text-[6px] font-semibold uppercase sm:p-2 sm:text-xs md:p-3 md:text-sm font-sfts-title ${
                                column.Header === 'Phone' ? 'min-w-[80px] sm:min-w-[120px]' :
                                column.Header === 'Acct Ref' ? 'min-w-[50px] sm:min-w-[80px]' :
                                column.Header === 'Date' ? 'min-w-[50px] sm:min-w-[80px]' :
                                'min-w-[40px] sm:min-w-[80px]'
                              }`}
                            >
                              {column.render('Header')}
                              <span className="ml-0.5 sm:ml-1">{column.isSorted ? (column.isSortedDesc ? ' Down' : ' Up') : ''}</span>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                  {page.map(row => {
                    prepareRow(row);
                    const { key, ...rowProps } = row.getRowProps();
                    return (
                      <tr key={key} {...rowProps} className="hover:bg-gray-50 transition-colors duration-200">
                        {row.cells.map(cell => {
                          const { key: cellKey, ...cellProps } = cell.getCellProps();
                          return (
                            <td
                              key={cellKey}
                              {...cellProps}
                              className={`p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm md:p-3 md:text-base font-sfts ${
                                cell.column.Header === 'Phone' ? 'min-w-[80px] sm:min-w-[120px] text-[6px] sm:text-xs' :
                                cell.column.Header === 'Acct Ref' ? 'min-w-[50px] sm:min-w-[80px]' :
                                cell.column.Header === 'Date' ? 'min-w-[50px] sm:min-w-[80px]' :
                                'min-w-[40px] sm:min-w-[80px] truncate'
                              }`}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pageOptions.length > 1 && (
          <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-1 sm:mt-3 sm:gap-2 md:mt-4 md:gap-3">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sfts"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600 sm:text-sm md:text-base font-sfts">
              Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
            </span>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sfts-medium"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modern Status Checking Modal */}
      {isModalOpen && modalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 overflow-y-auto">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden border border-gray-100 transform transition-all duration-300">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-5 py-4 flex justify-between items-center text-white">
              <h2 className="text-sm font-semibold tracking-wide font-sfts-title">Deriv Live Verification</h2>
              <button 
                onClick={() => { setIsModalOpen(false); setModalData(null); }}
                className="text-gray-300 hover:text-white transition-colors"
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              
              {/* API Status Badge */}
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-150">
                <div className={`p-2 rounded-full ${
                  modalData.derivLiveStatus === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  <FaCheckCircle size={20} />
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold font-sfts">Lifecycle Status</div>
                  <div className="text-sm font-bold text-gray-800 font-sfts-title">
                    {String(modalData.derivLiveStatus).toUpperCase()}
                  </div>
                </div>
              </div>

              {/* Amount Breakdown Cards */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                  <span className="text-[10px] text-gray-400 block mb-0.5">KES Amount</span>
                  <strong className="text-gray-800 text-sm">KES {modalData.amount?.toLocaleString()}</strong>
                </div>
                <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                  <span className="text-[10px] text-gray-400 block mb-0.5">USD Value</span>
                  <strong className="text-green-600 text-sm">${modalData.usdAmount?.toFixed(2)} USD</strong>
                </div>
              </div>

              {/* Secondary Details List */}
              <div className="text-xs space-y-2 border-t border-gray-100 pt-3 font-sfts">
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Account Reference:</span>
                  <span className="font-medium text-gray-800">{modalData.accountReference}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Beneficiary Mobile:</span>
                  <span className="font-medium text-gray-800">{modalData.phoneNumber ? `+${modalData.phoneNumber}` : '—'}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">REST Tracking ID:</span>
                  <span className="font-mono text-gray-600 bg-gray-50 px-1 rounded text-[10px]">{modalData.requestId || modalData._id || modalData.transactionId}</span>
                </div>
                {modalData.derivTransactionId && (
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Deriv Tx Ref:</span>
                    <span className="font-mono text-gray-600 text-[10px]">{modalData.derivTransactionId}</span>
                  </div>
                )}
              </div>

              {/* Control Buttons */}
              <div className="border-t border-gray-100 pt-4 flex gap-2 font-sfts">
                <button
                  onClick={() => { setIsModalOpen(false); setModalData(null); }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                {modalData.derivLiveStatus === 'complete' && (
                  <button
                    disabled={isModalPaying}
                    onClick={() => handleRetryB2C(null, modalData._id || modalData.transactionId)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-all flex justify-center items-center gap-1 shadow-md disabled:opacity-50"
                  >
                    {isModalPaying ? (
                      <>
                        <FaSpinner className="animate-spin mr-1" /> Sending...
                      </>
                    ) : (
                      'Pay M-Pesa B2C'
                    )}
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
      {isModalOpen && modalData && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-10 overflow-y-auto">
        <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden border border-gray-100 transform transition-all duration-300">
          
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-5 py-4 flex justify-between items-center text-white">
            <h2 className="text-sm font-semibold tracking-wide font-sfts-title">Deriv Live Verification</h2>
            <button 
              onClick={() => { setIsModalOpen(false); setModalData(null); }}
              className="text-gray-300 hover:text-white transition-colors"
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-5 space-y-4">
            
            {/* API Status Badge Matrix (Deriv + Database Status side by side) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2.5 bg-gray-50 p-2.5 rounded-lg border border-gray-150">
                <div className={`p-1.5 rounded-full ${
                  modalData.derivLiveStatus === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  <FaCheckCircle size={16} />
                </div>
                <div>
                  <div className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold font-sfts">Deriv Status</div>
                  <div className="text-xs font-bold text-gray-800 font-sfts-title">
                    {String(modalData.derivLiveStatus || 'pending').toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2.5 bg-gray-50 p-2.5 rounded-lg border border-gray-150">
                <div className={`p-1.5 rounded-full ${
                  (modalData.currentStatus || modalData.status) === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  <FaCheckCircle size={16} />
                </div>
                <div>
                  <div className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold font-sfts">Database Status</div>
                  <div className="text-xs font-bold text-gray-800 font-sfts-title">
                    {String(modalData.currentStatus || modalData.status || 'unknown').toUpperCase()}
                  </div>
                </div>
              </div>
            </div>

            {/* Amount Breakdown Cards */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                <span className="text-[10px] text-gray-400 block mb-0.5">KES Amount</span>
                <strong className="text-gray-800 text-sm">KES {modalData.amount?.toLocaleString()}</strong>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                <span className="text-[10px] text-gray-400 block mb-0.5">USD Value</span>
                <strong className="text-green-600 text-sm">${modalData.usdAmount?.toFixed(2)} USD</strong>
              </div>
            </div>

            {/* Secondary Details List */}
            <div className="text-xs space-y-2 border-t border-gray-100 pt-3 font-sfts">
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Account Reference:</span>
                <span className="font-medium text-gray-800">{modalData.accountReference}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Beneficiary Mobile:</span>
                <span className="font-medium text-gray-800">{modalData.phoneNumber ? `+${modalData.phoneNumber}` : '—'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">REST Tracking ID:</span>
                <span className="font-mono text-gray-600 bg-gray-50 px-1 rounded text-[10px]">{modalData.requestId || modalData._id || modalData.transactionId}</span>
              </div>
              {modalData.derivTransactionId && (
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Deriv Tx Ref:</span>
                  <span className="font-mono text-gray-600 text-[10px]">{modalData.derivTransactionId}</span>
                </div>
              )}
            </div>

            {/* Control Buttons */}
            <div className="border-t border-gray-100 pt-4 flex gap-2 font-sfts">
              <button
                onClick={() => { setIsModalOpen(false); setModalData(null); }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>

              {(() => {
                const localStatus = modalData.currentStatus || modalData.status;
                const isDerivComplete = modalData.derivLiveStatus === 'complete';
                
                // Disable if currently paying, if Deriv is not complete, or if DB status is already completed/failed
                const isPayoutDisabled = isModalPaying || !isDerivComplete || localStatus === 'completed' || localStatus === 'failed';

                return (
                  <button
                    disabled={isPayoutDisabled}
                    onClick={() => handleRetryB2C(null, modalData._id || modalData.transactionId)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-all flex justify-center items-center gap-1 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isModalPaying ? (
                      <>
                        <FaSpinner className="animate-spin mr-1" /> Sending...
                      </>
                    ) : localStatus === 'completed' ? (
                      'Already Completed'
                    ) : !isDerivComplete ? (
                      'Awaiting Deriv'
                    ) : (
                      'Pay M-Pesa B2C'
                    )}
                  </button>
                );
              })()}
            </div>

          </div>
        </div>
      </div>
    )}

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Open+Sans:wght@400&display=swap');
      `}</style>
    </div>
  );
};

export default Transactions;